import React, { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/joy';
import { CheckCircle, Lock } from '@phosphor-icons/react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const TRIAL_DAYS = 7;
const PRICE_PER_SEAT = 50; // USD/month per user

let stripePromise: Promise<Stripe | null> | null = null;
const getStripePromise = (): Promise<Stripe | null> => {
  if (!PUBLISHABLE_KEY) {
    return Promise.resolve(null);
  }
  if (!stripePromise) {
    stripePromise = loadStripe(PUBLISHABLE_KEY);
  }
  return stripePromise;
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export interface PaymentStepHandle {
  /**
   * Confirm the SetupIntent with the card details entered in PaymentElement.
   * On success, returns the Stripe PaymentMethod ID. On error, returns null
   * and the error is displayed inline by this component.
   */
  confirm: () => Promise<string | null>;
}

interface PaymentStepProps {
  /** True when the parent is submitting; disables interactions. */
  submitting: boolean;
}

/**
 * Internal form rendered inside <Elements>. Owns the PaymentElement and
 * exposes confirm() via ref to the parent (which fires it on "Complete Signup").
 */
const PaymentStepInner = forwardRef<PaymentStepHandle, { submitting: boolean }>(
  ({ submitting }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      confirm: async () => {
        setError(null);
        if (!stripe || !elements) {
          setError('Payment form not ready. Please wait a moment and try again.');
          return null;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Please check your card details.');
          return null;
        }
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        });
        if (confirmError) {
          setError(confirmError.message || 'Card was declined. Please try a different card.');
          return null;
        }
        if (!setupIntent || typeof setupIntent.payment_method !== 'string') {
          setError('Unexpected response from payment processor. Please try again.');
          return null;
        }
        return setupIntent.payment_method;
      },
    }));

    return (
      <Box>
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: { applePay: 'never', googlePay: 'never' },
            fields: { billingDetails: { address: 'auto' } },
          }}
        />
        {error && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {submitting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Securing your card with Stripe...</Typography>
          </Box>
        )}
      </Box>
    );
  }
);
PaymentStepInner.displayName = 'PaymentStepInner';

/**
 * Step 3 of the signup wizard: trial summary + Stripe PaymentElement.
 *
 * Fetches a SetupIntent client_secret from /api/signup/setup-intent/ on mount,
 * then mounts <Elements> + <PaymentElement>. Parent calls `ref.current.confirm()`
 * during form submit to get the resulting payment_method ID.
 */
export const PaymentStep = forwardRef<PaymentStepHandle, PaymentStepProps>(
  ({ submitting }, ref) => {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [initError, setInitError] = useState<string | null>(null);
    const stripePromiseRef = useMemo(() => getStripePromise(), []);

    // Today + 7-day trial. Computed once on mount so all displayed dates match.
    const { trialStart, trialEnd } = useMemo(() => {
      const start = new Date();
      const end = new Date(start);
      end.setDate(end.getDate() + TRIAL_DAYS);
      return { trialStart: start, trialEnd: end };
    }, []);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch('/api/signup/setup-intent/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || 'Unable to initialize payment form.');
          }
          const data = (await res.json()) as { client_secret: string };
          if (!cancelled) setClientSecret(data.client_secret);
        } catch (e: unknown) {
          if (!cancelled) {
            setInitError(e instanceof Error ? e.message : 'Unable to initialize payment form.');
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    if (!PUBLISHABLE_KEY) {
      return (
        <Alert color="danger">
          Payment processing is not configured. Please contact support.
        </Alert>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography level="h3" sx={{ mb: 0.5 }}>
            Add Your Payment Method
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            We won't charge you until your free trial ends.
          </Typography>
        </Box>

        {/* Trial summary */}
        <Card variant="soft" color="primary">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>
              🎉 Your 7-Day Free Trial
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm">Trial starts</Typography>
                <Typography level="body-sm" sx={{ fontWeight: 'md' }}>
                  Today — {formatDate(trialStart)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm">Trial ends</Typography>
                <Typography level="body-sm" sx={{ fontWeight: 'md' }}>
                  {formatDate(trialEnd)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm">First charge</Typography>
                <Typography level="body-sm" sx={{ fontWeight: 'md' }}>
                  {formatDate(trialEnd)}
                </Typography>
              </Box>
            </Stack>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 1.5 }}>
              <Box>
                <Typography level="body-sm">Price after trial</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  1 user × ${PRICE_PER_SEAT}/user/month — add more users later and your bill scales.
                </Typography>
              </Box>
              <Typography level="title-lg">${PRICE_PER_SEAT}.00</Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Cancellation reassurance */}
        <Alert color="success" startDecorator={<CheckCircle />}>
          <Box>
            <Typography level="title-sm">Cancel any time before {formatDate(trialEnd)}</Typography>
            <Typography level="body-sm">
              Cancel from <strong>Settings → Billing</strong> before your trial ends and you won't
              be charged a cent.
            </Typography>
          </Box>
        </Alert>

        {/* Card form */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Lock size={18} color="var(--joy-palette-text-tertiary)" />
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Your card details are encrypted and securely stored by Stripe. ApexOS never sees your full card number.
            </Typography>
          </Box>

          {initError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {initError}
            </Alert>
          )}

          {!clientSecret && !initError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {clientSecret && (
            <Elements
              stripe={stripePromiseRef}
              options={{
                clientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <PaymentStepInner ref={ref} submitting={submitting} />
            </Elements>
          )}
        </Box>
      </Stack>
    );
  }
);
PaymentStep.displayName = 'PaymentStep';
